import React, { useState } from 'react';
import { buildShareLinks, copyToClipboard } from '../../utils/shareLinks';
import { LinkIcon } from '../../assets/icons';
import './SocialShareButtons.css';

/**
 * SocialShareButtons — the standard outbound share icon row
 * (Facebook / Twitter / Reddit / WhatsApp / Telegram) plus a copy-link
 * button that shows a brief "Copied!" confirmation.
 *
 * Renders the same `.social-icons` / `.social-icon` markup NovelHeader used
 * inline, so existing styles apply wherever the row is dropped in.
 *
 * Props:
 *   - url: absolute URL to share
 *   - title: share text title
 *   - showCopy: render the copy-link button (default true)
 *   - copyLabel: accessible label for the copy button
 */
const SocialShareButtons = ({ url, title, showCopy = true, copyLabel = 'Copy link', className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={`social-icons social-icons--inline${className ? ` ${className}` : ''}`}>
      {buildShareLinks(url, title).map(({ href, label, className: platform, Icon }) => (
        <a
          key={platform}
          href={href}
          className={`social-icon ${platform}`}
          aria-label={label}
          title={label}
          target="_blank"
          rel="noreferrer"
        >
          <Icon className="icon icon--social" />
        </a>
      ))}
      {showCopy && (
        <button
          type="button"
          className="social-icon social-icon--copy"
          aria-label={copied ? 'Link copied' : copyLabel}
          title={copied ? 'Copied!' : copyLabel}
          onClick={handleCopy}
        >
          {copied ? (
            <span className="social-icon__copied" aria-hidden="true">✓</span>
          ) : (
            <LinkIcon className="icon icon--social" />
          )}
        </button>
      )}
    </div>
  );
};

export default SocialShareButtons;
